import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { OffTransfer } from 'modules/warehouse';
import { useObjectCard } from '../context/ObjectCardProvider';
import Proposal2Warehouse, {
    type Proposal2WarehouseTarget,
} from '../Proposal2Warehouse';
import useAddProductsTransfer from '../Tabs/ProductsTableBody/model/useAddProductsTransfer';
import ShortageFooter from './ShortageFooter';
import ShortageHeader from './ShortageHeader';
import ShortageTable from './ShortageTable';
import useProposalShortage from './model/useProposalShortage';
import type { ProposalShortageKind } from './model/types';

interface ProposalShortageBlockProps {
    kind: ProposalShortageKind;
}

const ProposalShortageBlock = ({ kind }: ProposalShortageBlockProps) => {
    const { t } = useTranslation(['Objects', 'Document']);
    const { isEditLocked } = useObjectCard();
    const { loading, proposalName, generation, items, hasShortage } =
        useProposalShortage(kind);
    const [expanded, setExpanded] = useState(false);
    const [warehouseTarget, setWarehouseTarget] =
        useState<Proposal2WarehouseTarget | null>(null);
    const {
        canAdd,
        sources,
        teamName,
        objectWarehouseId,
        showTransfer,
        setShowTransfer,
        sourceWId,
        sourceReservObjectId,
        openTransfer,
        handleFetchStocks,
    } = useAddProductsTransfer();

    if (loading || !hasShortage) return null;

    const title =
        kind === 'products'
            ? t('proposalShortage.titleProducts')
            : kind === 'other'
              ? t('proposalShortage.titleOther')
              : t('proposalShortage.titleServices');
    const hint = proposalName
        ? t('proposalShortage.hint', { name: proposalName })
        : t('proposalShortage.hintDefault');
    const allMissing = items.every((item) => item.status === 'missing');
    const tone = allMissing ? 'shortage' : 'partial';
    const showActions =
        !isEditLocked &&
        (kind === 'services' || kind === 'other' || (kind === 'products' && canAdd));
    const canOpenWarehouse =
        !isEditLocked &&
        Boolean(generation) &&
        (generation?.source || 'object') === 'lead';

    const handleOpenWarehouse = () => {
        if (!generation || !canOpenWarehouse) return;
        setWarehouseTarget({
            generationId: generation.ID,
            title: generation.proposalName || t('Document:card.noName'),
            products: Array.isArray(generation.products) ? generation.products : [],
            additionalItems: Array.isArray(generation.additionalItems)
                ? generation.additionalItems
                : [],
            pricesInUsd: Boolean(generation.proposalUsd),
        });
    };

    return (
        <div
            className={`object-analytics-payments object-analytics-payments--${tone} proposal-shortage${
                expanded ? ' proposal-shortage--expanded' : ' proposal-shortage--collapsed'
            }`}
            role="status"
        >
            <div className="object-analytics-payments__glow" aria-hidden="true" />

            <ShortageHeader
                title={title}
                hint={hint}
                expanded={expanded}
                allMissing={allMissing}
                onToggle={() => setExpanded((prev) => !prev)}
            />

            {kind === 'products' && sourceWId > 0 && (
                <OffTransfer
                    wId={sourceWId}
                    teamId={null}
                    reservObjectId={sourceReservObjectId}
                    fixedToWarehouseId={objectWarehouseId}
                    fetchStocks={handleFetchStocks}
                    show={showTransfer}
                    setShow={setShowTransfer}
                />
            )}

            <Proposal2Warehouse
                showTarget={warehouseTarget}
                setShowTarget={setWarehouseTarget}
            />

            {expanded && (
                <>
                    <ShortageTable
                        kind={kind}
                        items={items}
                        proposalName={proposalName}
                        showActions={showActions}
                        canAdd={canAdd}
                        sources={sources}
                        teamName={teamName}
                        openTransfer={openTransfer}
                    />
                    {canOpenWarehouse && (
                        <ShortageFooter onOpenWarehouse={handleOpenWarehouse} />
                    )}
                </>
            )}
        </div>
    );
};

export default ProposalShortageBlock;
